import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { StatCard, Section, PageHeader } from "@/components/dashboard/DashboardPrimitives";
import ReportsPanel from "@/components/dashboard/ReportsPanel";
import PaymentsPanel from "@/components/dashboard/PaymentsPanel";
import BulkScreeningPanel from "@/components/dashboard/BulkScreeningPanel";
import ApiKeysPanel from "@/components/dashboard/ApiKeysPanel";
import QuizResultsPanel from "@/components/dashboard/QuizResultsPanel";
import PersonalCodesPanel from "@/components/dashboard/PersonalCodesPanel";

const ROLE_LABEL = {
  super_admin: "Super Admin", admin: "Internal Admin",
  school_admin: "School Admin", class_teacher: "Class Teacher",
  doctor: "Doctor", parent: "Parent",
};

/**
 * Shared by Super Admin & Internal Admin. Super Admin gets extra mutation buttons.
 * Internal Admin is read-only.
 */
export default function AdminDashboard({ user }) {
  const isSuper = user.role === "super_admin";
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [schools, setSchools] = useState([]);
  const [audit, setAudit] = useState([]);
  const [health, setHealth] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(null);

  const loadAll = async () => {
    try {
      const [s, u, sc, a, h] = await Promise.all([
        api.get("/api/admin/stats"),
        api.get("/api/admin/users", { params: { limit: 20, search: search || undefined, role: roleFilter || undefined } }),
        api.get("/api/admin/schools", { params: { limit: 20 } }),
        api.get("/api/admin/audit-log", { params: { limit: 20 } }),
        api.get("/api/admin/health"),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setUsersTotal(u.data.total);
      setSchools(sc.data.schools);
      setAudit(a.data.entries);
      setHealth(h.data);
    } catch (e) {
      console.error("admin load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const searchUsers = async (e) => {
    e?.preventDefault?.();
    try {
      const u = await api.get("/api/admin/users", { params: { limit: 20, search: search || undefined, role: roleFilter || undefined } });
      setUsers(u.data.users);
      setUsersTotal(u.data.total);
    } catch (e2) {
      alert(formatApiError(e2));
    }
  };

  const toggleActivation = async (targetUser) => {
    if (!isSuper) return;
    const action = targetUser.is_active ? "deactivate" : "activate";
    if (!window.confirm(`${action[0].toUpperCase() + action.slice(1)} ${targetUser.email}?`)) return;
    setMutating(targetUser.user_id);
    try {
      await api.patch(`/api/admin/users/${targetUser.user_id}/${action}`);
      await loadAll();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setMutating(null);
    }
  };

  if (loading) return <div className="text-slate-400 text-sm" data-testid="admin-loading">Loading…</div>;

  return (
    <>
      <PageHeader
        user={user}
        subtitle={isSuper
          ? "Platform-wide break-glass access. All actions audited."
          : "Platform visibility. User data is read-only; actions require Super Admin."}
      />

      {/* Platform Stats */}
      <Section title="Platform stats" testid="admin-stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total users" value={stats.total_users} testid="stat-total-users" />
          <StatCard label="Active users" value={stats.active_users} accent="emerald" />
          <StatCard label="Total reports" value={stats.total_reports} accent="sky" />
          <StatCard label="Reports (30d)" value={stats.reports_last_30d} accent="sky" />
          <StatCard label="Schools" value={stats.total_schools} />
          <StatCard label="Parents" value={stats.users_by_role.parent || 0} />
          <StatCard label="Doctors" value={stats.users_by_role.doctor || 0} />
          <StatCard label="Class Teachers" value={stats.users_by_role.class_teacher || 0} />
        </div>
      </Section>

      {/* Trend (inline SVG sparkline — no deps) */}
      <Section title="Last 7 days" description="Signups & reports per day" testid="admin-trend-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TrendCard label="Signups" data={stats.signup_trend_7d} accent="#38BDF8" />
          <TrendCard label="Reports generated" data={stats.reports_trend_7d} accent="#34D399" />
        </div>
      </Section>

      {/* Users */}
      <Section
        title="Users"
        description={`${usersTotal} total · showing up to 20`}
        testid="admin-users-section"
        right={
          <form onSubmit={searchUsers} className="flex items-center gap-2">
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or name"
              data-testid="admin-users-search"
              className="px-3 py-1.5 text-sm rounded bg-[#0E1A3A] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none"
            />
            <select
              value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
              data-testid="admin-users-role-filter"
              className="px-3 py-1.5 text-sm rounded bg-[#0E1A3A] border border-slate-700 text-slate-100 outline-none"
            >
              <option value="">All roles</option>
              {["super_admin","admin","school_admin","class_teacher","doctor","parent"].map(r =>
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              )}
            </select>
            <button type="submit" className="px-3 py-1.5 text-sm rounded bg-sky-500 hover:bg-sky-400 text-white" data-testid="admin-users-search-submit">
              Search
            </button>
          </form>
        }
      >
        <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 overflow-hidden">
          <div className="divide-y divide-slate-800/80">
            {users.length === 0 && <p className="p-6 text-sm text-slate-400">No users match.</p>}
            {users.map(u => (
              <div key={u.user_id} className="p-4 flex items-center justify-between gap-4" data-testid={`user-row-${u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-100 truncate">{u.full_name}</span>
                    <span className="text-[10px] uppercase tracking-wider bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                    {!u.is_active && (
                      <span className="text-[10px] uppercase bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded">inactive</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{u.email}</p>
                  {u.quiz && (
                    <p className="text-xs mt-1 flex flex-wrap gap-x-3" data-testid={`user-quiz-${u.user_id}`}>
                      <span className={u.quiz.result === "high_performer" ? "text-amber-200" : "text-teal-300"}>
                        Quiz {u.quiz.score}/{u.quiz.total} · {u.quiz.result === "high_performer" ? "High Performer" : "Good"}{u.quiz.attempts > 1 ? ` · ${u.quiz.attempts} tests` : ""}
                      </span>
                      {u.quiz.code && <span className="text-slate-400 font-mono">{u.quiz.code}</span>}
                    </p>
                  )}
                </div>
                {isSuper && u.role !== "super_admin" && (
                  <button
                    onClick={() => toggleActivation(u)}
                    disabled={mutating === u.user_id}
                    data-testid={`user-toggle-${u.user_id}`}
                    className={`text-xs px-2.5 py-1.5 rounded border ${
                      u.is_active
                        ? "bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border-rose-500/30"
                        : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 border-emerald-500/30"
                    } disabled:opacity-60`}
                  >
                    {mutating === u.user_id ? "…" : u.is_active ? "Deactivate" : "Activate"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Schools */}
      <Section title="Schools" description={`${schools.length} on platform`} testid="admin-schools-section">
        <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 divide-y divide-slate-800/80">
          {schools.length === 0 && <p className="p-6 text-sm text-slate-400">No schools yet.</p>}
          {schools.map(s => (
            <div key={s.school_id} className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {s.school_logo_path && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/auth/logo/${s.school_logo_path}`}
                    alt="" className="w-10 h-10 rounded bg-slate-900 object-contain flex-shrink-0"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-100 truncate">{s.school_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{s.school_board} · {s.teacher_count} teacher{s.teacher_count === 1 ? "" : "s"} · {s.report_count} reports</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Audit log */}
      <Section title="Audit log" description="Last 20 sensitive actions" testid="admin-audit-section">
        <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 divide-y divide-slate-800/80">
          {audit.length === 0 && <p className="p-6 text-sm text-slate-400">No actions logged yet.</p>}
          {audit.map(e => (
            <div key={e.audit_id} className="p-3 text-xs text-slate-300 flex items-start gap-3">
              <span className="text-slate-500 w-32 flex-shrink-0">{new Date(e.created_at).toLocaleString()}</span>
              <span className="text-sky-300 font-mono w-40 flex-shrink-0">{e.action}</span>
              <span className="text-slate-400 flex-1 truncate">
                by <strong className="text-slate-200">{e.actor_email}</strong>
                {e.target_email && <> → <strong className="text-slate-200">{e.target_email}</strong></>}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* System health */}
      <Section title="System health" testid="admin-health-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard label="Database" value={health?.database?.toUpperCase()} accent={health?.database === "ok" ? "emerald" : "rose"} />
          <StatCard label="ONNX model" value={health?.onnx_model} accent={health?.onnx_model === "loaded" ? "emerald" : "amber"} />
          <StatCard label="Last check" value={health ? new Date(health.checked_at).toLocaleTimeString() : "—"} />
        </div>
      </Section>

      {/* Reports (platform-wide for admins) */}
      <Section title="All reports" description="Platform-wide — click delete to remove" testid="admin-reports-section">
        <ReportsPanel userRole={user.role} />
      </Section>

      {/* Bulk Screening (super_admin + admin) */}
      <BulkScreeningPanel />

      {/* Partner API keys (list: admin + super; create/revoke: super) */}
      <QuizResultsPanel />

      <PersonalCodesPanel />

      <ApiKeysPanel isSuper={isSuper} />

      {/* Payments — super admin only */}
      {isSuper && (
        <Section title="" testid="admin-payments-section">
          <PaymentsPanel />
        </Section>
      )}
    </>
  );
}

function TrendCard({ label, data, accent }) {
  const max = Math.max(...data.map(d => d.count), 1);
  const w = 300, h = 60, step = w / (data.length - 1 || 1);
  const points = data.map((d, i) => `${i * step},${h - (d.count / max) * h}`).join(" ");
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="rounded-lg bg-[#0E1A3A] border border-slate-800 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-slate-300">{label}</p>
        <p className="text-xl font-semibold text-slate-100">{total}</p>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14">
        <polyline fill="none" stroke={accent} strokeWidth="2" points={points} />
      </svg>
      <div className="flex justify-between text-[10px] text-slate-500 mt-1">
        {data.map(d => <span key={d.date}>{d.date.slice(-5)}</span>)}
      </div>
    </div>
  );
}
